import axios from "axios";

// Asset documents are asset-scoped, not hierarchy-scoped: the routes live under
// /api/assets/{assetNodeId}/documents (AssetDocumentsController), NOT under the
// /api/asset-hierarchy base used by cmmsAssetHierarchy.jsx.
const assetDocumentsUrl = (assetNodeId) => `/api/assets/${assetNodeId}/documents`;

const throwResponseData = (error) => {
  throw error.response?.data ?? error;
};

export const fetchAssetDocuments = async (companyKey, assetNodeId, documentType = null) => {
  try {
    const response = await axios.get(assetDocumentsUrl(assetNodeId), {
      // documentType is optional; axios omits undefined params, so a null filter
      // must not be forwarded as an empty string.
      params: { companyKey, documentType: documentType == null ? undefined : Number(documentType) },
    });
    return response.data;
  } catch (error) {
    throwResponseData(error);
  }
};

export const fetchAssetDocument = async (companyKey, assetNodeId, documentId) => {
  try {
    const response = await axios.get(`${assetDocumentsUrl(assetNodeId)}/${documentId}`, {
      params: { companyKey },
    });
    return response.data;
  } catch (error) {
    throwResponseData(error);
  }
};

export const uploadAssetDocument = async (
  assetNodeId,
  { companyKey, documentType, title, description, file, onProgress }
) => {
  try {
    const formData = new FormData();
    formData.append("companyKey", companyKey);
    // No uploadedByUserId: the server stamps the uploader from the token, so the
    // client cannot attribute a document to somebody else.
    // Send the numeric enum value — the controller binds AssetDocumentType, not a name.
    formData.append("documentType", Number(documentType));
    formData.append("title", title);
    if (description != null) {
      formData.append("description", description);
    }
    formData.append("file", file);

    const response = await axios.post(assetDocumentsUrl(assetNodeId), formData, {
      headers: { "Content-Type": "multipart/form-data" },
      onUploadProgress: onProgress,
    });
    return response.data;
  } catch (error) {
    throwResponseData(error);
  }
};

export const downloadAssetDocument = async (companyKey, assetNodeId, documentId, fileName) => {
  try {
    const response = await axios.get(`${assetDocumentsUrl(assetNodeId)}/${documentId}/download`, {
      params: { companyKey },
      responseType: "blob",
    });
    const blob = new Blob([response.data], { type: response.headers["content-type"] });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName ?? `document-${documentId}`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  } catch (error) {
    throwResponseData(error);
  }
};

// The file itself, for showing on the page. An <img src> pointing at the download route
// cannot carry the bearer token, so the bytes come through axios and the caller turns them
// into an object URL.
export const fetchAssetDocumentBlob = async (companyKey, assetNodeId, documentId) => {
  try {
    const response = await axios.get(`${assetDocumentsUrl(assetNodeId)}/${documentId}/download`, {
      params: { companyKey },
      responseType: "blob",
    });
    return new Blob([response.data], { type: response.headers["content-type"] });
  } catch (error) {
    throwResponseData(error);
  }
};

export const updateAssetDocument = async (
  assetNodeId,
  documentId,
  { companyKey, documentType, title, description }
) => {
  try {
    const response = await axios.put(`${assetDocumentsUrl(assetNodeId)}/${documentId}`, {
      companyKey,
      documentType: Number(documentType),
      title,
      description,
    });
    return response.data;
  } catch (error) {
    throwResponseData(error);
  }
};

export const deleteAssetDocument = async (companyKey, assetNodeId, documentId) => {
  try {
    const response = await axios.delete(`${assetDocumentsUrl(assetNodeId)}/${documentId}`, {
      params: { companyKey },
    });
    return response.data;
  } catch (error) {
    throwResponseData(error);
  }
};
